/*
 * Power Package builder (Polar Diesel)
 * Guided, step-by-step builder. On "Add to cart" each chosen part is added as
 * its own cart line via /cart/add.js (items array), tagged with the visible
 * line item property "Power Package" and a hidden "_build_id".
 * All content (steps, parts, help text) comes from the theme editor.
 */
(function () {
  if (window.customElements.get('power-package-builder')) return;

  var VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/i;

  function formatMoney(cents, format) {
    var fmt = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
    var value = (Number(cents) || 0) / 100;
    function withDelims(num, decimals, thousands, decimal) {
      var parts = num.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelims(value, 2, "'", '.');
        default: return withDelims(value, 2, ',', '.');
      }
    });
  }

  function makeBuildId() {
    return 'PP-' + Date.now().toString(36).toUpperCase() + '-' + Math.random().toString(36).slice(2, 6).toUpperCase();
  }

  class PowerPackageBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.setup();
    }

    setup() {
      this.packageName = (this.dataset.packageName || '').trim() || document.title;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.afterAdd = this.dataset.afterAdd || 'cart';
      this.answersOn = this.dataset.answersOn || 'first';
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.cartAddUrl = (this.dataset.cartAddUrl || '/cart/add') + '.js';
      this.badgeText = (this.dataset.badgeText || '').trim();

      this.steps = Array.prototype.map.call(this.querySelectorAll('[data-ppb-step]'), (el, i) => this.parseStep(el, i));
      if (!this.steps.length) return;

      this.progressEl = this.querySelector('[data-ppb-progress]');
      this.reviewEl = this.querySelector('[data-ppb-review]');
      this.reviewListEl = this.querySelector('[data-ppb-review-list]');
      this.addErrorEl = this.querySelector('[data-ppb-add-error]');
      this.summaryEl = this.querySelector('[data-ppb-summary]');
      this.totalEl = this.querySelector('[data-ppb-total]');
      this.totalMobileEl = this.querySelector('[data-ppb-total-mobile]');
      this.countMobileEl = this.querySelector('[data-ppb-count-mobile]');
      this.backBtn = this.querySelector('[data-ppb-back]');
      this.nextBtn = this.querySelector('[data-ppb-next]');
      this.nextMobileBtn = this.querySelector('[data-ppb-next-mobile]');
      this.addBtn = this.querySelector('[data-ppb-add]');

      this.current = 0;
      this.reviewing = false;
      this.maxVisited = 0;

      this.backBtn.addEventListener('click', () => this.back());
      this.nextBtn.addEventListener('click', () => this.next());
      if (this.nextMobileBtn) this.nextMobileBtn.addEventListener('click', () => (this.reviewing ? this.addToCart() : this.next()));
      this.addBtn.addEventListener('click', () => this.addToCart());

      // Pre-select the first available part where staff asked for it.
      this.steps.forEach((step) => {
        if (step.type !== 'parts' || !step.preselect) return;
        var first = step.options.find((o) => o.variant && o.variant.available);
        if (first) first.selected = true;
      });

      this.steps.forEach((step) => {
        if (step.type === 'questions') {
          step.fields.forEach((f) => f.addEventListener('input', () => { this.clearError(step); }));
        }
      });

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          var idx = this.steps.findIndex((s) => s.id === (e.detail && e.detail.blockId));
          if (idx > -1) {
            this.reviewing = false;
            this.current = idx;
            this.maxVisited = Math.max(this.maxVisited, idx);
            this.update();
          }
        });
      }

      this.update(false);
    }

    /* ---------- parsing ---------- */

    parseStep(el, index) {
      var step = {
        el: el,
        index: index,
        id: el.dataset.stepId,
        type: el.dataset.type,
        title: el.dataset.title || 'Step',
        required: el.dataset.required === 'true',
        mode: el.dataset.mode === 'multiple' ? 'multiple' : 'single',
        max: parseInt(el.dataset.max, 10) || 0,
        preselect: el.dataset.preselect === 'true',
        skipIf: new Set((el.dataset.skipIf || '').split(',').filter(Boolean)),
        errorEl: el.querySelector('[data-ppb-error]'),
        countEl: el.querySelector('[data-ppb-count]'),
        hintEl: el.querySelector('[data-ppb-hint]'),
        options: [],
        fields: [],
      };
      if (step.mode === 'single') step.max = 1;
      if (step.type === 'parts') {
        el.querySelectorAll('[data-ppb-option]').forEach((optEl) => step.options.push(this.parseOption(optEl, step)));
      } else {
        step.fields = Array.prototype.slice.call(el.querySelectorAll('[data-ppb-answer]'));
      }
      return step;
    }

    parseOption(el, step) {
      var variants = [];
      var names = [];
      try { variants = JSON.parse(el.querySelector('[data-ppb-variants]').textContent); } catch (e) { variants = []; }
      try { names = JSON.parse(el.querySelector('[data-ppb-option-names]').textContent); } catch (e) { names = []; }
      var opt = {
        el: el,
        step: step,
        productId: el.dataset.productId,
        title: el.dataset.productTitle,
        defaultOnly: el.dataset.defaultOnly === 'true',
        houseBrand: el.dataset.houseBrand === 'true',
        variants: variants,
        names: names,
        selected: false,
        variant: variants.find((v) => v.available) || variants[0] || null,
        selects: [],
        pick: el.querySelector('[data-ppb-pick]'),
        priceEl: el.querySelector('[data-ppb-price]'),
        img: el.querySelector('.ppb-option__img'),
      };
      opt.defaultImg = opt.img ? opt.img.getAttribute('src') : null;

      if (variants.length > 1 && !opt.defaultOnly) this.buildSelects(opt, el.querySelector('[data-ppb-selects]'));

      opt.pick.addEventListener('click', () => this.toggle(opt));
      return opt;
    }

    buildSelects(opt, container) {
      var count = (opt.variants[0].options || []).length;
      for (let i = 0; i < count; i++) {
        var values = [];
        opt.variants.forEach((v) => { if (values.indexOf(v.options[i]) === -1) values.push(v.options[i]); });
        if (values.length < 2) { opt.selects.push(null); continue; }
        var id = 'ppb-' + opt.step.id + '-' + opt.productId + '-' + i;
        var wrap = document.createElement('div');
        wrap.className = 'ppb-select';
        var label = document.createElement('label');
        label.setAttribute('for', id);
        label.textContent = opt.names[i] || 'Option';
        var select = document.createElement('select');
        select.id = id;
        select.className = 'ppb-input';
        values.forEach((val) => {
          var o = document.createElement('option');
          o.value = val;
          o.textContent = val;
          select.appendChild(o);
        });
        select.value = opt.variant ? opt.variant.options[i] : values[0];
        select.addEventListener('change', () => this.onSelectChange(opt, i));
        wrap.appendChild(label);
        wrap.appendChild(select);
        container.appendChild(wrap);
        opt.selects.push(select);
      }
    }

    currentValues(opt) {
      return opt.selects.map((s, i) => (s ? s.value : opt.variant ? opt.variant.options[i] : null));
    }

    onSelectChange(opt, changedIndex) {
      var values = this.currentValues(opt);
      var match = opt.variants.find((v) => v.options.every((val, i) => val === values[i]));
      if (!match) {
        // Keep the value just picked, pick the best matching combination for the rest.
        var candidates = opt.variants.filter((v) => v.options[changedIndex] === values[changedIndex]);
        match = candidates.find((v) => v.available) || candidates[0] || null;
        if (match) opt.selects.forEach((s, i) => { if (s) s.value = match.options[i]; });
      }
      opt.variant = match;
      if (opt.selected && (!match || !match.available)) {
        opt.selected = false;
        this.showError(opt.step, 'That option is sold out, so it was removed from your build. Please choose another.');
      } else if (match && match.available && !opt.selected && opt.step.mode === 'single' && opt.step.options.some((o) => o.selected) === false) {
        // Picking options on a card counts as choosing that card.
        opt.selected = true;
      }
      this.update(false);
    }

    /* ---------- selection ---------- */

    selectedIn(step) {
      return step.options.filter((o) => o.selected);
    }

    toggle(opt) {
      var step = opt.step;
      this.clearError(step);
      if (opt.selected) {
        opt.selected = false;
      } else {
        if (!opt.variant || !opt.variant.available) {
          this.showError(step, 'Sorry, that option is sold out.');
          return;
        }
        if (step.mode === 'single') {
          step.options.forEach((o) => { o.selected = false; });
        } else if (step.max && this.selectedIn(step).length >= step.max) {
          this.showError(step, 'You can choose up to ' + step.max + ' in this step. Remove one to pick another.');
          return;
        }
        opt.selected = true;
      }
      this.update(false);
    }

    computeSkips() {
      var skipped = [];
      this.steps.forEach((step, i) => {
        var skip = false;
        if (step.skipIf.size) {
          for (var j = 0; j < i; j++) {
            if (skipped[j]) continue;
            if (this.steps[j].options.some((o) => o.selected && step.skipIf.has(String(o.productId)))) { skip = true; break; }
          }
        }
        skipped[i] = skip;
        step.skipped = skip;
        if (skip) step.options.forEach((o) => { o.selected = false; });
      });
    }

    activeSteps() {
      return this.steps.filter((s) => !s.skipped);
    }

    /* ---------- validation ---------- */

    validate(step, show) {
      var msg = '';
      if (step.type === 'parts') {
        var chosen = this.selectedIn(step);
        if (step.required && chosen.length === 0) msg = 'Please choose an option to continue.';
        else if (chosen.some((o) => !o.variant || !o.variant.available)) msg = 'One of your choices is sold out. Please pick another option.';
      } else {
        for (var f of step.fields) {
          var val = (f.value || '').trim();
          if (f.required && !val) { msg = 'Please fill in "' + f.dataset.label + '".'; break; }
          if (val && f.dataset.kind === 'vin' && !VIN_RE.test(val)) { msg = 'Please enter a valid 17-character VIN.'; break; }
        }
      }
      if (show) { msg ? this.showError(step, msg) : this.clearError(step); }
      return !msg;
    }

    showError(step, msg) {
      if (step && step.errorEl) step.errorEl.textContent = msg;
    }

    clearError(step) {
      if (step && step.errorEl) step.errorEl.textContent = '';
    }

    /* ---------- navigation ---------- */

    nextActiveIndex(from) {
      for (var i = from + 1; i < this.steps.length; i++) if (!this.steps[i].skipped) return i;
      return -1;
    }

    prevActiveIndex(from) {
      for (var i = from - 1; i >= 0; i--) if (!this.steps[i].skipped) return i;
      return -1;
    }

    next() {
      if (this.reviewing) return;
      var step = this.steps[this.current];
      if (!this.validate(step, true)) return;
      var n = this.nextActiveIndex(this.current);
      if (n === -1) this.reviewing = true;
      else {
        this.current = n;
        this.maxVisited = Math.max(this.maxVisited, n);
      }
      this.update(true);
    }

    back() {
      if (this.reviewing) {
        this.reviewing = false;
        var last = this.prevActiveIndex(this.steps.length);
        this.current = last === -1 ? 0 : last;
      } else {
        var p = this.prevActiveIndex(this.current);
        if (p === -1) return;
        this.current = p;
      }
      this.update(true);
    }

    goTo(index, toReview) {
      // Moving forward: every step in between must be complete.
      var target = toReview ? this.steps.length : index;
      if (target > this.current || this.reviewing) {
        var start = this.reviewing ? 0 : this.current;
        for (var i = start; i < target; i++) {
          var s = this.steps[i];
          if (s.skipped) continue;
          if (!this.validate(s, true)) {
            this.reviewing = false;
            this.current = i;
            this.update(true);
            return;
          }
        }
      }
      this.reviewing = !!toReview;
      if (!toReview) this.current = index;
      this.maxVisited = Math.max(this.maxVisited, toReview ? this.steps.length : index);
      this.update(true);
    }

    /* ---------- rendering ---------- */

    update(scroll) {
      this.computeSkips();
      if (!this.reviewing && this.steps[this.current] && this.steps[this.current].skipped) {
        var n = this.nextActiveIndex(this.current);
        if (n === -1) n = this.prevActiveIndex(this.current);
        this.current = n === -1 ? 0 : n;
      }
      var active = this.activeSteps();
      var pos = active.indexOf(this.steps[this.current]);
      var totalSteps = active.length + 1; // + review

      this.steps.forEach((step) => {
        var isCurrent = !this.reviewing && step === this.steps[this.current];
        step.el.hidden = !isCurrent;
        step.el.classList.toggle('is-current', isCurrent);
        if (step.countEl) step.countEl.textContent = 'Step ' + (active.indexOf(step) + 1) + ' of ' + totalSteps;
        if (step.type === 'parts') {
          if (step.hintEl) step.hintEl.textContent = this.hintFor(step);
          step.options.forEach((o) => this.renderOption(o));
        }
      });
      this.reviewEl.hidden = !this.reviewing;
      if (this.reviewing) this.renderReview();

      this.renderProgress(active);
      this.renderSummary(active);

      // Nav buttons
      this.backBtn.hidden = !this.reviewing && pos <= 0;
      this.nextBtn.hidden = this.reviewing;
      this.addBtn.hidden = !this.reviewing;
      if (!this.reviewing) {
        var step = this.steps[this.current];
        var isLast = this.nextActiveIndex(this.current) === -1;
        var nothingChosen = step.type === 'parts' && !step.required && this.selectedIn(step).length === 0;
        var label = isLast ? 'Review build' : nothingChosen ? 'Skip this step' : 'Next';
        this.nextBtn.textContent = label;
        if (this.nextMobileBtn) this.nextMobileBtn.textContent = label;
      } else if (this.nextMobileBtn) {
        this.nextMobileBtn.textContent = 'Add to cart';
      }

      if (scroll) {
        var rect = this.getBoundingClientRect();
        if (rect.top < 0 || rect.top > window.innerHeight * 0.4) {
          var y = window.scrollY + rect.top - 80;
          window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
        }
      }
    }

    hintFor(step) {
      var base;
      if (step.mode === 'single') base = 'Choose one.';
      else if (step.max) base = 'Choose up to ' + step.max + '.';
      else base = 'Choose as many as you like.';
      return step.required ? base : base + ' Optional, skip it if you don\u2019t need it.';
    }

    renderOption(opt) {
      var v = opt.variant;
      var soldOut = !v || !v.available;
      opt.el.classList.toggle('is-selected', opt.selected);
      opt.el.classList.toggle('is-sold-out', soldOut);
      opt.pick.setAttribute('aria-pressed', opt.selected ? 'true' : 'false');
      if (opt.priceEl) {
        if (!v) opt.priceEl.textContent = 'Unavailable';
        else {
          var html = formatMoney(v.price, this.moneyFormat);
          opt.priceEl.innerHTML = '';
          if (v.compare && v.compare > v.price) {
            var s = document.createElement('s');
            s.className = 'ppb-option__compare';
            s.textContent = formatMoney(v.compare, this.moneyFormat);
            opt.priceEl.appendChild(s);
            opt.priceEl.appendChild(document.createTextNode(' '));
          }
          opt.priceEl.appendChild(document.createTextNode(html));
        }
      }
      if (opt.img && v && v.image) {
        opt.img.setAttribute('src', v.image);
        opt.img.removeAttribute('srcset');
      } else if (opt.img && opt.defaultImg && opt.img.getAttribute('src') !== opt.defaultImg && !(v && v.image)) {
        opt.img.setAttribute('src', opt.defaultImg);
      }
      // Mark sold out values in dropdowns
      if (opt.selects.length) {
        var values = this.currentValues(opt);
        opt.selects.forEach((sel, i) => {
          if (!sel) return;
          Array.prototype.forEach.call(sel.options, (o) => {
            var ok = opt.variants.some((vv) => vv.available && vv.options[i] === o.value &&
              vv.options.every((val, k) => k === i || !opt.selects[k] || val === values[k]));
            o.textContent = o.value + (ok ? '' : ' (sold out)');
          });
        });
      }
    }

    renderProgress(active) {
      if (!this.progressEl) return;
      this.progressEl.innerHTML = '';
      var items = active.map((s) => ({ step: s }));
      items.push({ review: true });
      items.forEach((item, i) => {
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ppb__progress-btn';
        var isCurrent = item.review ? this.reviewing : !this.reviewing && item.step === this.steps[this.current];
        var done = !item.review && (item.step.index < this.current || this.reviewing) && this.validate(item.step, false);
        if (isCurrent) { li.className = 'is-current'; btn.setAttribute('aria-current', 'step'); }
        else if (done) li.className = 'is-done';
        var num = document.createElement('span');
        num.className = 'ppb__progress-num';
        num.textContent = String(i + 1);
        var label = document.createElement('span');
        label.className = 'ppb__progress-label';
        label.textContent = item.review ? 'Review' : item.step.title;
        btn.appendChild(num);
        btn.appendChild(label);
        var reachable = item.review ? this.maxVisited >= this.steps.length : item.step.index <= this.maxVisited;
        if (!reachable && !isCurrent) btn.classList.add('is-locked');
        btn.addEventListener('click', () => {
          if (item.review) this.goTo(null, true);
          else this.goTo(item.step.index, false);
        });
        li.appendChild(btn);
        this.progressEl.appendChild(li);
      });
    }

    buildLines() {
      var lines = [];
      this.activeSteps().forEach((step) => {
        this.selectedIn(step).forEach((o) => { if (o.variant) lines.push({ step: step, opt: o, variant: o.variant }); });
      });
      return lines;
    }

    makeBadge(opt) {
      if (!this.badgeText || !opt.houseBrand) return null;
      var b = document.createElement('span');
      b.className = 'ppb-badge ppb-badge--small';
      b.textContent = this.badgeText;
      return b;
    }

    renderSummary(active) {
      var lines = this.buildLines();
      var total = lines.reduce((sum, l) => sum + l.variant.price, 0);
      this.summaryEl.innerHTML = '';
      if (!lines.length) {
        var empty = document.createElement('li');
        empty.className = 'ppb__summary-empty';
        empty.textContent = 'Nothing picked yet.';
        this.summaryEl.appendChild(empty);
      }
      lines.forEach((l) => {
        var li = document.createElement('li');
        li.className = 'ppb__summary-item';
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'ppb__summary-link';
        var stepName = document.createElement('span');
        stepName.className = 'ppb__summary-step';
        stepName.textContent = l.step.title;
        var name = document.createElement('span');
        name.className = 'ppb__summary-part';
        name.textContent = l.opt.title + (l.opt.defaultOnly || l.opt.variants.length < 2 ? '' : ' \u2013 ' + l.variant.title);
        var price = document.createElement('span');
        price.className = 'ppb__summary-price';
        price.textContent = formatMoney(l.variant.price, this.moneyFormat);
        b.appendChild(stepName);
        b.appendChild(name);
        var badge = this.makeBadge(l.opt);
        if (badge) b.appendChild(badge);
        b.addEventListener('click', () => this.goTo(l.step.index, false));
        li.appendChild(b);
        li.appendChild(price);
        this.summaryEl.appendChild(li);
      });
      var totalText = formatMoney(total, this.moneyFormat);
      this.totalEl.textContent = totalText;
      if (this.totalMobileEl) this.totalMobileEl.textContent = totalText;
      if (this.countMobileEl) this.countMobileEl.textContent = lines.length + (lines.length === 1 ? ' part' : ' parts');
      this.addBtn.disabled = !lines.length;
    }

    renderReview() {
      var lines = this.buildLines();
      this.reviewListEl.innerHTML = '';
      if (!lines.length) {
        this.reviewListEl.textContent = 'You have not picked any parts yet.';
        return;
      }
      var table = document.createElement('ul');
      table.className = 'ppb-review__items list-unstyled';
      lines.forEach((l) => {
        var li = document.createElement('li');
        li.className = 'ppb-review__item';
        li.innerHTML = '<span class="ppb-review__step"></span><span class="ppb-review__part"></span><span class="ppb-review__price"></span>';
        li.children[0].textContent = l.step.title;
        li.children[1].textContent = l.opt.title + (l.opt.variants.length < 2 ? '' : ' \u2013 ' + l.variant.title);
        var rb = this.makeBadge(l.opt);
        if (rb) li.children[1].appendChild(rb);
        li.children[2].textContent = formatMoney(l.variant.price, this.moneyFormat);
        table.appendChild(li);
      });
      var total = lines.reduce((sum, l) => sum + l.variant.price, 0);
      var tot = document.createElement('li');
      tot.className = 'ppb-review__item ppb-review__total';
      tot.innerHTML = '<span></span><span>Total</span><span></span>';
      tot.children[2].textContent = formatMoney(total, this.moneyFormat);
      table.appendChild(tot);
      this.reviewListEl.appendChild(table);

      var answers = this.collectAnswers();
      var keys = Object.keys(answers);
      if (keys.length) {
        var dl = document.createElement('dl');
        dl.className = 'ppb-review__answers';
        keys.forEach((k) => {
          var dt = document.createElement('dt'); dt.textContent = k;
          var dd = document.createElement('dd'); dd.textContent = answers[k];
          dl.appendChild(dt); dl.appendChild(dd);
        });
        this.reviewListEl.appendChild(dl);
      }
    }

    collectAnswers() {
      var answers = {};
      this.activeSteps().forEach((step) => {
        if (step.type !== 'questions') return;
        step.fields.forEach((f) => {
          var val = (f.value || '').trim();
          if (!val) return;
          if (f.dataset.kind === 'vin') val = val.toUpperCase();
          answers[f.dataset.label] = val;
        });
      });
      return answers;
    }

    /* ---------- cart ---------- */

    buildPayload() {
      var buildId = makeBuildId();
      var answers = this.collectAnswers();
      var items = this.buildLines().map((l, i) => {
        var props = { 'Power Package': this.packageName, _build_id: buildId };
        if (this.answersOn === 'all' || i === 0) Object.keys(answers).forEach((k) => { props[k] = answers[k]; });
        return { id: Number(l.variant.id), quantity: 1, properties: props };
      });
      return { items: items, buildId: buildId };
    }

    addToCart() {
      // Re-check every step before adding.
      for (var s of this.activeSteps()) {
        if (!this.validate(s, true)) {
          this.reviewing = false;
          this.current = s.index;
          this.update(true);
          return;
        }
      }
      var built = this.buildPayload();
      if (!built.items.length) return;
      var body = { items: built.items };
      var drawer = document.querySelector('cart-drawer');
      var useDrawer = this.afterAdd === 'drawer' && drawer && typeof drawer.renderContents === 'function';
      if (useDrawer) {
        body.sections = drawer.getSectionsToRender().map((sec) => sec.id);
        body.sections_url = window.location.pathname;
      }
      this.addErrorEl.textContent = '';
      this.addBtn.disabled = true;
      this.addBtn.classList.add('loading');
      this.dispatchEvent(new CustomEvent('ppb:add', { bubbles: true, detail: built }));
      fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then((r) => r.json().then((json) => ({ ok: r.ok, json: json })))
        .then((res) => {
          if (!res.ok || res.json.status) {
            throw new Error(res.json.description || res.json.message || 'Could not add your Power Package to the cart.');
          }
          if (useDrawer) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(res.json);
            if (window.publish && window.PUB_SUB_EVENTS) window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'power-package-builder' });
          } else {
            window.location.href = this.cartUrl;
          }
        })
        .catch((err) => {
          this.addErrorEl.textContent = err.message + ' Nothing was added. Please adjust your build and try again.';
        })
        .finally(() => {
          this.addBtn.disabled = false;
          this.addBtn.classList.remove('loading');
        });
    }
  }

  window.customElements.define('power-package-builder', PowerPackageBuilder);
})();
